/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// libraries
import React from 'react';
import {
    makeStyles,
} from '@material-ui/core';
import {
    array,
    func,
    bool,
} from 'prop-types';
import Slider from 'react-slick';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import LinkOrATag from '../../../GraphqlCommonComponents/LinkOrATag/LinkOrATag';
import ResponsiveImage from '../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import replaceSpecialChar from '../../../../../helpers/replaceSpecialChar';
import noop from '../../../../../helpers/noop';

const useStyles = makeStyles((theme) => ({

    main: {
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        '& .slick-track': {
            display: 'flex',
            justifyContent: 'center',
        },
        '& .slick-slide': {
            display: 'inline-block',
            padding: '0 10px',
            position: 'relative',
        },
    },
    sliderContainer: (props) => ({
        '& .slick-list': {
            overflow: 'inherit',
            margin: '0 0px',
        },
        '& .slick-track': {
            margin: '0 auto',
        },
        '& .slick-prev, & .slick-next': {
            transform: 'translate(0,-50%)',
            cursor: 'pointer',
            border: 'none',
            outline: 0,
            fontSize: 0,
            lineHeight: 0,
            top: '42%',
            width: '16px',
            height: '30px',
            position: 'absolute',
            background: 'transparent',
        },
        '& .slick-prev': {
            left: 0,
        },
        '& .slick-next': {
            right: 0,
        },
        '& .slick-dots': {
            listStyle: 'none',
            display: 'flex !important',
            padding: '0',
            justifyContent: 'center',
            margin: '0',
            '& li': {
                lineHeight: '1',
            },
            '& .slick-active': {
                '& div': {
                    background: theme.palette.cms?.sliderNavDotSelectedBackground || 'rgb(47, 47, 47)',
                },
            },
            '& .slick-current': {
                '& div': {
                    paddingRight: '5px',
                },
            },
        },
        '& .slick-disabled': {
            color: '#cdd0d3',
            cursor: 'default',
        },
        [theme.breakpoints.between(602, 770)]: {
            '& .slick-track': {
                width: props.totalNumberOfProducts <= 3 && '100% !important',
            },
            '& .slick-slide': {
                maxWidth: props.totalNumberOfProducts <= 3 && 'calc(33.3%) !important',
            },
        },

        [theme.breakpoints.up(1000)]: {
            padding: '0 70px',
            '& .slick-prev, & .slick-next': {
                width: '25px',
            },
        },
        [theme.breakpoints.up(600)]: {
            padding: '0 30px',
            '& .slick-prev, & .slick-next': {
                width: '25px',
            },
        },
        [theme.breakpoints.down('xs')]: {
            padding: '0px',
            '& .slick-prev, & .slick-next': {
                width: '25px',
            },
        },
    }),
    gridContainer: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    image: {
        width: '100%',
    },
    header: {
        fontSize: '20px',
        lineHeight: '25px',
        marginBottom: '16px',
        paddingLeft: '40px',
        '@media screen and (max-width: 1024px)': {
            marginTop: '40px',
        },
    },
    panelItem: {
        color: theme.palette.cms?.black || '#000000',
        textAlign: 'center',
        fontSize: '14px',
        lineHeight: '19px',
    },
    linkPanel: {
        textDecoration: 'none',
    },
}));

const PlaRelatedCollection = ({
    data, trackClickThrough, isPersonalizedProp,
}) => {
    const imgFormatter = (imgObj) => {
        if (isPersonalizedProp) {
            const newImgPath = (imgObj?.path && imgObj?.name) ? `${imgObj?.path}${imgObj?.name}x.jpg?auto=webp` : '';
            return newImgPath;
        }
        return null;
    };

    // Smooth scroll behavior by managing vertical v horizontal scrolling when customer interacts with recs container
    if (!data || data?.length === 0) return null;
    const classes = useStyles();
    const title = data?.title || data?.linking?.title;
    return (
        <div className={`${classes.gridContainer}`}>
            <strong className={classes.header}>Related Collections</strong>
            <Slider
                dots={false}
                speed={800}
                slidesToShow={4}
                slidesToScroll={4}
                swipe
                draggable={false}
                infinite={false}
                nextArrow={<ArrowForwardIosIcon />}
                prevArrow={<ArrowBackIosIcon />}
                className={`
        ${classes.main}
        ${classes.sliderContainer}
    `}
                responsive={[
                    {
                        breakpoint: 769,
                        settings: {
                            slidesToShow: 3,
                            slidesToScroll: 3,
                        },
                    },
                ]}
            >
                {data?.map((panelItem) => (
                    <LinkOrATag
                        handleCallback={null}
                        className={`${classes.linkPanel}`}
                        href={panelItem?.linking?.link?.href || panelItem?.path}
                        isPersonalized={isPersonalizedProp}
                        trackClickThrough={trackClickThrough}
                    >
                        <div className={classes.mainContainer}>

                            <ResponsiveImage
                                className={classes.image}
                                path={imgFormatter(panelItem?.image) || panelItem?.image?.url}
                                alt={(panelItem?.image?.description || panelItem?.image?.name) || 'Item Image'}
                            />

                            {((panelItem?.title || panelItem?.name) || (panelItem?.linking?.title || panelItem?.path)) && (<div className={`${classes.panelItem} nup-v3-panel-item`}> {replaceSpecialChar((panelItem?.title || panelItem?.name) || (panelItem?.linking?.title || panelItem?.path), true)} </div>)}
                        </div>
                    </LinkOrATag>
                ))}
            </Slider>
        </div>
    );
};

PlaRelatedCollection.propTypes = {
    data: array,
    trackClickThrough: func,
    isPersonalizedProp: bool,
};

PlaRelatedCollection.defaultProps = {
    data: [],
    trackClickThrough: noop,
    isPersonalizedProp: false,
};

export default PlaRelatedCollection;
